"use client";

import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "../../../lib/cn";
import { Button } from "../../../primitives/Button";

/**
 * PanelHeader — the shared back-arrow header used by every source panel
 * (Paste / URL / YouTube / RSS / Email / Drive). A ghost-icon back button +
 * a title + an optional description, matching the donor's per-panel
 * `DialogHeader` shape but factored out so all panels read as one family.
 *
 * The title renders as an `<h2>` (the panels live inside a Dialog whose
 * `DialogTitle` names the surface; per-panel headings are section headings).
 * The back button is labelled for screen readers.
 */
export interface PanelHeaderProps {
  /** Panel title. */
  title: React.ReactNode;
  /** One-line description under the title. */
  description?: React.ReactNode;
  /** Back-to-grid handler. */
  onBack: () => void;
  /** Accessible label for the back button. Default "Back to sources". */
  backLabel?: string;
  className?: string;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  description,
  onBack,
  backLabel = "Back to sources",
  className,
}) => (
  <div className={cn("flex items-start gap-2", className)}>
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={onBack}
      aria-label={backLabel}
      className="mt-0.5 shrink-0"
    >
      <ArrowLeft className="size-4" aria-hidden />
    </Button>
    <div className="min-w-0">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
    </div>
  </div>
);
PanelHeader.displayName = "PanelHeader";

/**
 * PanelFooter — the shared right-aligned action row (Cancel + primary CTA)
 * with a top hairline, matching the donor's panel footers.
 */
export interface PanelFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const PanelFooter: React.FC<PanelFooterProps> = ({ children, className }) => (
  <div className={cn("flex justify-end gap-2 border-t border-border pt-4", className)}>{children}</div>
);
PanelFooter.displayName = "PanelFooter";
